/**
 *  Class to handle single image slider
 *  
 *  Constructor excepts single container div that holds all of the slider's functionality.
 *  This container div keeps the functionality containerized to this instance, so multiple
 *  sliders can exist on the same page without conflict.
 * 
 *  Images should have the class name .slideshow-image-container
 */
export default class Slider {

    constructor(imageContainer){
        this.imageContainer = imageContainer;
        this.images = imageContainer.querySelectorAll('.slideshow-image-container');
        this.buttonLeft = imageContainer.querySelector('.button-left');
        this.buttonRight = imageContainer.querySelector('.button-right');
        this.currentSlide = 0;
        this.startX = 0;                // For touch sliding functionality
        this.moveX = 0;                 // For touch sliding functionality

        // Config
        this.centeringOffset = 8;       // % Units
        this.smallBreakPoint = 768      // px Units

        this.initialize();
    }

    /**
     *  Basically adding event listeners, and then calling this.showSlide(0) to run the functionality on page load
     */
    initialize(){
        let self = this;
        this.images.forEach(function(image){
            image.addEventListener('click', function(){
                self.showSlide(this.dataset.imageIndex);
            });
        });

        this.imageContainer.addEventListener('touchstart', event => {
            this.initiateTouch(event);
        });

        this.imageContainer.addEventListener('touchmove', event => {
            this.touchMove(event);
        });

        this.imageContainer.addEventListener('touchend', event => {
            this.endTouch(event);
        });

        this.buttonLeft.addEventListener('click', () => {
            this.showPreviousSlide();
        });

        this.buttonRight.addEventListener('click', () => {
            this.showNextSlide();
        });
        
        // Render first slide on start
        this.showSlide(0);
    }

    /**
     *  The meat of the functionality. Takes the index of the slide to show as a parameter
     */
    showSlide(index){
        // Make sure the slider index isn't out of bounds
        if(index > this.images.length - 1){
            index = this.images.length - 1; 
        } else if(index < 0){
            index = 0;
        }
        this.currentSlide = index;

        // Make the left and right buttons appear & disappear
        // Need to use setTimeout to make the opacity fading work...
        if(this.images.length > 1){
            if(index == 0){
            this.hideLeftButton();
            this.showRightButton();
            } else if(index == this.images.length - 1){
                this.hideRightButton();
                this.showLeftButton();
            } else {
                this.showLeftButton();
                this.showRightButton();
            }
        } else {
            // Only one image, no buttons necessary
            this.hideRightButton();
            this.hideLeftButton();
        }
    
        // Remove little slider sidebars if the page is too small
        let width = screen.width;
        let centeringOffset = this.centeringOffset;
    
        if(width < this.smallBreakPoint){
            centeringOffset = 0;
        }
    
        let offset = 0;
        let image;

        // Main loop. Apply 'active' class to the currently active slide, and remove it from all others that may have it
        // Also apply an offset to each image, so that they all move left and right together
        for(let i = 0; i < this.images.length; i++){
            image = this.images[i];
            offset = -index * 100 + centeringOffset + '%';
        
            image.className = image.className.replace(/ active/g, '');
            image.style.left = offset;
        
            if(i == index){   
                image.className += ' active';
            }
        }
    }

    showNextSlide(){
        this.showSlide(this.currentSlide + 1);
    }

    showPreviousSlide(){
        this.showSlide(this.currentSlide - 1);
    }

    initiateTouch(event){
        this.startX = event.touches[0].pageX;
    }

    /**
     *  Called in rapid succession on finger slide
     */
    touchMove(event){
        // This prevents the user from being able to vertically scroll the page while using the slider,
        // helping make it more smooth. 
        event.preventDefault();
  
        // Calculate the amount the image should move
        // Using square roots here to make the movement feel more native or natural
        if(this.startX >= event.touches[0].pageX){
            this.moveX = -Math.pow(this.startX - event.touches[0].pageX, 0.5) * 7.5;
        } else {
            this.moveX = Math.pow(event.touches[0].pageX - this.startX, 0.5) * 7.5;
        }

        let image;

        // Apply the translation
        for(let i = 0; i < this.images.length; i++){
            image = this.images[i];

            image.style.transform = 'translateX(' + this.moveX + 'px)';
        }
    }

    /**
     *  This function is called at the end of the slide. Most importantly, this function must return the 
     *  slider to a viewable state, ie, the slider must look like it's set on one slide, and not stuck in
     *  between due to the finger dragging and translations from touchMove. 
     */
    endTouch(event){
        let divWidth = event.target.scrollWidth;

        let image;

        // Add transition for 'translate' property, and move the div's back to center
        for(let i = 0; i < this.images.length; i++){
            image = this.images[i];

            image.style.transition = 'left cubic-bezier(.25,.46,.45,.94) 500ms, height ease-in-out 500ms, top ease-in-out 500ms, filter ease-in-out 500ms, transform 400ms cubic-bezier(.25,.46,.45,.94)';
            image.style.transform = 'translateX(0)';
        }

        // This setTimeout() will run after the animation from the previous for() loop has completed
        let self = this;
        setTimeout(() => {
            for(let i = 0; i < self.images.length; i++){
            image = self.images[i];
        
            // Here we must remove the transition for 'translate', because if not, the touchMove() translations will be 500ms delayed
            image.style.transition = 'left cubic-bezier(.25,.46,.45,.94) 500ms, height ease-in-out 500ms, top ease-in-out 500ms, filter ease-in-out 500ms';
            }
        }, 500);

        // If we dragged far enough to change slides ( > 20% of the div's width), show the previous or next slide
        if(this.moveX > divWidth * 0.2){
            this.showPreviousSlide();
            this.moveX = 0;
        } else if(this.moveX < divWidth * -0.2){
            this.showNextSlide();
            this.moveX = 0;
        }
    }

    showButton(button){
        button.style.display = 'block';
        button.style.opacity = 0;

        setTimeout(() => {
            button.style.opacity = 1;
        }, 10);
    }

    hideButton(button){
        button.style.opacity = 0;

        setTimeout(() => {
            button.style.display = 'none';
        }, 500);
    }

    showLeftButton(){
        this.showButton(this.buttonLeft);
    }

    showRightButton(){
        this.showButton(this.buttonRight);
    }

    hideLeftButton(){
        this.hideButton(this.buttonLeft);
    }

    hideRightButton(){
        this.hideButton(this.buttonRight);
    }
}


// WEBPACK FOOTER //
// ./scripts/class/Slider.js